// =====================================================================
// NfseCell (NF-e/NFS-e Focus / S97) — célula da coluna "NF" na aba Pagamentos.
// Cores padrão do app (primary do tema), layout refinado. Estados: sem nota →
// "Emitir NF"; emitindo → progresso; autorizado → nº + PDF; rejeitado → motivo +
// reemitir. Padrão MUI v4.
// =====================================================================
import React, { useState } from "react";
import {
  Button, IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Typography, Box
} from "@material-ui/core";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import RefreshIcon from "@material-ui/icons/Refresh";
import CancelIcon from "@material-ui/icons/Cancel";
import { toast } from "react-toastify";
import {
  emitNfse, refreshFiscalNote, cancelFiscalNote, openFiscalPdf, openFiscalXml, financeErrorMessage
} from "../../services/financeService";

const ERR = "#e57373"; // vermelho suave (erro), padrão MUI red[300]

const NfseCell = ({ payment, note, defaultDiscriminacao, onChanged }) => {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [disc, setDisc] = useState("");
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const openDlg = () => {
    setDisc(payment.notes || defaultDiscriminacao || "Referente a consulta médica");
    setDlgOpen(true);
  };
  const doEmit = async () => {
    setBusy(true);
    try {
      await emitNfse(payment.id, disc);
      toast.success("Nota enviada à prefeitura. Acompanhe o status aqui.");
      setDlgOpen(false);
      onChanged && onChanged();
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const doRefresh = async () => {
    try { await refreshFiscalNote(note.id); onChanged && onChanged(); }
    catch (e) { toast.error(financeErrorMessage(e)); }
  };
  // nome amigável do arquivo pro cliente: "NFS-e 16 - Nome do Paciente.pdf"
  const niceName = (ext) => {
    const nome = ((payment.patient && payment.patient.fullName) || "cliente").replace(/[\\/:*?"<>|]+/g, "").trim();
    const num = note && note.numero ? ` ${note.numero}` : "";
    return `NFS-e${num} - ${nome}.${ext}`;
  };
  const doPdf = async () => { try { await openFiscalPdf(note.id, niceName("pdf")); } catch (e) { toast.error(financeErrorMessage(e)); } };
  const doXml = async () => { try { await openFiscalXml(note.id, niceName("xml")); } catch (e) { toast.error(financeErrorMessage(e)); } };
  const doCancel = async () => {
    const just = cancelReason.trim();
    if (just.length < 15) { toast.error("A justificativa precisa ter ao menos 15 caracteres."); return; }
    setBusy(true);
    try {
      await cancelFiscalNote(note.id, just);
      toast.success("Nota cancelada na prefeitura.");
      setCancelOpen(false);
      onChanged && onChanged();
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const emitDialog = (
    <Dialog open={dlgOpen} onClose={busy ? undefined : () => setDlgOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle style={{ fontWeight: 600, letterSpacing: 0.2 }}>Emitir nota fiscal · NFS-e</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {payment.patient?.fullName || "Paciente"} — valor da nota:{" "}
          <Typography component="span" color="primary" style={{ fontWeight: 700 }}>
            {Number(payment.amount).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
          </Typography>
        </Typography>
        <TextField
          label="Discriminação do serviço"
          value={disc}
          onChange={(e) => setDisc(e.target.value)}
          fullWidth multiline margin="dense"
          helperText="O que consta na nota (ex.: Referente a consulta médica)."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDlgOpen(false)} disabled={busy}>Cancelar</Button>
        <Button
          variant="contained" color="primary" onClick={doEmit} disabled={busy || !disc.trim()}
          startIcon={busy ? <CircularProgress size={16} /> : null}
          style={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
        >
          Emitir nota
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Sem nota → botão emitir
  if (!note) {
    return (
      <>
        <Button
          size="small" variant="outlined" color="primary" onClick={openDlg}
          style={{ textTransform: "none", fontWeight: 600, fontSize: 12, letterSpacing: 0.2, borderRadius: 8, padding: "1px 12px" }}
        >
          Emitir NF
        </Button>
        {emitDialog}
      </>
    );
  }

  // Autorizada → nº + PDF/XML + cancelar
  if (note.status === "autorizado") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 3 }}>
        <Tooltip title={`NFS-e nº ${note.numero || ""} autorizada — abrir PDF`}>
          <IconButton size="small" color="primary" onClick={doPdf}><PictureAsPdfIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Typography component="span" color="primary" style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: 0.3 }}>
          nº {note.numero || "—"}
        </Typography>
        <Tooltip title="Baixar XML">
          <Button size="small" color="primary" onClick={doXml} style={{ minWidth: 0, padding: "0 5px", fontSize: 11, textTransform: "none", opacity: 0.8 }}>XML</Button>
        </Tooltip>
        <Tooltip title="Cancelar esta nota na prefeitura">
          <IconButton size="small" onClick={() => { setCancelReason(""); setCancelOpen(true); }} style={{ color: ERR, padding: 4 }}>
            <CancelIcon style={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Dialog open={cancelOpen} onClose={busy ? undefined : () => setCancelOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle style={{ fontWeight: 600 }}>Cancelar NFS-e nº {note.numero || ""}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              O cancelamento é enviado à prefeitura e é <strong>definitivo</strong>. Informe o motivo (a prefeitura exige, 15 a 255 caracteres).
            </Typography>
            <TextField
              label="Justificativa do cancelamento" value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth multiline margin="dense" inputProps={{ maxLength: 255 }}
              helperText={`${cancelReason.trim().length}/255 — mínimo 15 caracteres`}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelOpen(false)} disabled={busy}>Voltar</Button>
            <Button variant="contained" onClick={doCancel} disabled={busy || cancelReason.trim().length < 15}
              startIcon={busy ? <CircularProgress size={16} /> : null}
              style={{ textTransform: "none", fontWeight: 600, boxShadow: "none", background: ERR, color: "#fff" }}>
              Cancelar nota
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Rejeitada → motivo + reemitir
  if (note.status === "erro") {
    const msg = Array.isArray(note.erros)
      ? note.erros.map((e) => e.mensagem || e.correcao || JSON.stringify(e)).join("; ")
      : (note.erros && note.erros.mensagem) || "Rejeitada pela prefeitura.";
    return (
      <>
        <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 6 }}>
          <Tooltip title={msg}>
            <Chip size="small" label="Rejeitada"
              style={{ background: "rgba(229,115,115,0.14)", color: ERR, border: "1px solid rgba(229,115,115,0.4)", fontWeight: 600, height: 22 }} />
          </Tooltip>
          <Button size="small" color="primary" onClick={openDlg} style={{ textTransform: "none", fontSize: 12 }}>Reemitir</Button>
        </Box>
        {emitDialog}
      </>
    );
  }

  // enviando / processando / cancelado
  const cancel = note.status === "cancelado";
  return (
    <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 5 }}>
      {!cancel && <CircularProgress size={13} color="primary" />}
      <Typography component="span" color="textSecondary" style={{ fontSize: 12 }}>{cancel ? "Cancelada" : "Emitindo…"}</Typography>
      {!cancel && (
        <Tooltip title="Atualizar status">
          <IconButton size="small" color="primary" onClick={doRefresh}><RefreshIcon style={{ fontSize: 16 }} /></IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default NfseCell;
